// src/components/common/PrimaryCtaButtons.tsx
// The primary homepage calls to action, rendered inside the hero.
'use client';

import Link from 'next/link';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackFunnelEvent, FUNNEL_EVENTS } from '@/lib/analytics/events';
import {
  CTA_GREEN,
  CTA_PRIMARY,
  CTA_PRIMARY_HOVER,
  WHATSAPP_MESSAGE,
  WHATSAPP_NUMBER,
} from '@/lib/brand/ctaColors';

/**
 * PrimaryCtaButtons — the hero CTA stack.
 *
 *   1. "Get Your Business Found — Start Free"  → /choose-your-industry  (blue #1B93EA)
 *   2. "Have a question? Ask us here."         → wa.me deep link        (ghost, grey outline)
 *
 * Each label owns its own destination, icon and analytics event. Only the order
 * and the colour slot are design decisions — never the links.
 *
 * ## Why the chooser leads
 *
 * The hero used to carry a "Looking for a business? Search here" text link,
 * which mixed buyer intent into a page whose whole promise is "be found" — a
 * seller proposition. The two intents work against each other above the fold, so
 * search came out of the hero.
 *
 * The blue button leads because it is the one question that unblocks a
 * first-time owner: they do not yet know which of the four lanes is theirs, and
 * that uncertainty is a common reason people abandon a signup flow. The chooser
 * hands them to the correct vertical's landing page, where plan selection and
 * the application form already live.
 *
 * ## Why WhatsApp is a ghost button
 *
 * It is the lowest-friction option available to an owner who is not ready to
 * fill in a form — a wa.me tap opens a conversation rather than a 40-field page.
 * But it is a question channel, not the conversion path, so it is styled as a
 * light grey outline: clearly visible, deliberately quieter than the blue CTA.
 *
 * ## Why the pink Shop Local button is gone
 *
 * The stack used to carry a third, pink "Shop Local" button (→ /shop-local) as
 * the demand side of the marketplace. It has been removed from the hero for now
 * — three equal-weight buttons diluted the primary CTA. /shop-local stays live
 * and linked from the sitemap; where the marketplace CTA returns (possibly the
 * mobile menu) is a pending design decision.
 *
 * ## Layout
 *
 * Stacked and full-width, including inside the hero's left column on desktop.
 * That is deliberate: on a phone — where most South African traffic lands —
 * stacked full-width buttons are the pattern that gets tapped.
 *
 * The soft drop-shadow sits on the first button because it marks the top slot
 * of the stack. If the order changes, it moves with it.
 *
 * Contrast is documented in src/lib/brand/ctaColors.ts.
 */
export function PrimaryCtaButtons() {
  return (
    <div className="mt-5 mb-6 flex w-full flex-col gap-3">
      <Button
        asChild
        size="lg"
        className="w-full text-base font-bold shadow-[0_10px_24px_-12px_rgba(27,147,234,0.65)]"
        style={{ backgroundColor: CTA_PRIMARY, color: '#FFFFFF' }}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = CTA_PRIMARY_HOVER)}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = CTA_PRIMARY)}
        onClick={() => trackFunnelEvent(FUNNEL_EVENTS.selectVertical, { cta: 'choose_my_industry' })}
      >
        <Link href="/choose-your-industry">Get Your Business Found &mdash; Start Free</Link>
      </Button>

      <Button
        asChild
        variant="outline"
        size="lg"
        className="w-full border-gray-300 bg-white text-base font-semibold text-slate-700 shadow-none hover:border-gray-400 hover:bg-gray-50 hover:text-slate-900"
        onClick={() => trackFunnelEvent(FUNNEL_EVENTS.whatsappClick, { cta: 'hero_whatsapp' })}
      >
        <Link
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="flex items-center justify-center gap-2">
            <MessageCircle aria-hidden="true" className="h-5 w-5" style={{ color: CTA_GREEN }} />
            Have a question? Ask us here.
          </span>
        </Link>
      </Button>
    </div>
  );
}
